import { FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { CheckoutExtensionUI, UrgencyBoostExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { capitalize } from '@utils/string-utils';
import { TypedRuleGroup } from '@app/sharedModule/components/rules-dynamic-form/model';

export { UrgencyBoostExtension };

export const USAGE_TOKEN_PREFIX = 'urgency_boost_';
export const USAGE_TOKEN_RANDOM_LENGTH = 12;
export const DEFAULT_INTERNAL_NAME = UrgencyBoostExtension.DEFAULT_INTERNAL_NAME;
export const DEFAULT_INTERNAL_DESCRIPTION = '';

export enum UrgencyBoostTab {
  SETTINGS = 'settings',
  BOOSTS = 'boosts',
  DISPLAY_CONDITIONS = 'display_conditions',
}

export const BADGE_TYPE_OPTIONS = [
  { value: UrgencyBoostExtension.BadgeType.LOW_STOCK, label: 'Low stock indicator' },
  { value: UrgencyBoostExtension.BadgeType.RECENT_PURCHASES, label: 'Recent Purchases' },
  { value: UrgencyBoostExtension.BadgeType.CUSTOM_MESSAGE, label: 'Custom Message' },
];

export const WINDOW_UNIT_OPTIONS: { value: UrgencyBoostExtension.RecentPurchasesWindowUnit; label: string }[] = [
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

export const BADGE_CORNER_RADIUS_OPTIONS = Object.values(CheckoutExtensionUI.CornerRadius).map((value) => ({
  label: capitalize(value),
  value,
}));

export const DEFAULT_SPACE_ABOVE_BADGE = '';

export const BADGE_ICON_OPTIONS: { key: string; iconClass: string; label: string }[] = [
  { key: 'hot', iconClass: 'hot', label: 'Hot' },
  { key: 'alarm', iconClass: 'alarm', label: 'Alarm' },
  { key: 'lightning', iconClass: 'lightning', label: 'Fast' },
  { key: 'hourglass', iconClass: 'hourglass', label: 'Limited time' },
  { key: 'bullseye', iconClass: 'bullseye', label: 'Target' },
];

export const PRODUCTS_IN_CART_MODE_OPTIONS: { value: UrgencyBoostExtension.ProductsInCartMode; label: string }[] = [
  { value: 'smart', label: 'Smart Selection' },
  { value: 'manual', label: 'Manual Selection' },
];

export interface ManualProductRefFormGroup {
  id: FormControl<string | number>;
  name: FormControl<string>;
  imageUrl: FormControl<string>;
}

export interface ProductsInCartFormGroup {
  mode: FormControl<UrgencyBoostExtension.ProductsInCartMode>;
  rules: FormArray<FormGroup<TypedRuleGroup>>;
  products: FormControl<UrgencyBoostExtension.ManualProductRef[]>;
  productsMatchMode: FormControl<UrgencyBoostExtension.ProductsMatchMode>;
}

export interface BadgeFormGroup {
  id: FormControl<string>;
  type: FormControl<UrgencyBoostExtension.BadgeType>;
  richTextTemplate: FormControl<string>;
  icon: FormControl<string>;
  customIconUrl: FormControl<string | File | null>;
  backgroundColor: FormControl<string>;
  textColor: FormControl<string>;
  cornerRadius: FormControl<CheckoutExtensionUI.CornerRadius>;
  // discriminated extras (added/removed when type changes)
  name?: FormControl<string>;
  minStock?: FormControl<number>;
  maxStock?: FormControl<number>;
  minPurchases?: FormControl<number>;
  windowValue?: FormControl<number>;
  windowUnit?: FormControl<UrgencyBoostExtension.RecentPurchasesWindowUnit>;
}

export interface BoostGroupFormGroup {
  id: FormControl<number>;
  includeDigitalItems: FormControl<boolean>;
  productsInCart: FormGroup<ProductsInCartFormGroup>;
  badges: FormArray<FormGroup<BadgeFormGroup>>;
}

export interface LayoutFormGroup {
  spaceAboveBadge: FormControl<string>;
}

export interface UrgencyBoostFormGroup {
  usageToken: FormControl<string>;
  internalName: FormControl<string>;
  internalDescription: FormControl<string>;
  maxBadgesPerProduct: FormControl<number | null>;
  layout: FormGroup<LayoutFormGroup>;
  boostGroups: FormArray<FormGroup<BoostGroupFormGroup>>;
  restrictionsRules: FormArray<FormGroup<TypedRuleGroup>>;
}

export const DEFAULT_BOOST_GROUP: Omit<UrgencyBoostExtension.BoostGroup, 'id'> = {
  includeDigitalItems: UrgencyBoostExtension.DEFAULT_BOOST_GROUP_INCLUDE_DIGITAL_ITEMS,
  productsInCart: { mode: 'smart', rules: [], products: [], productsMatchMode: 'all' },
  badges: [],
};

export function makeBadgeId(): string {
  return `badge_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Single source of truth for building a `FormGroup<BoostGroupFormGroup>`.
 * Callers pass a `buildRulesFormArray` closure (a thin wrapper around
 * `RulesDynamicFormService.initRulesFormArray`) so this factory can stay
 * service-free and live in `model.ts` next to the form interfaces.
 */
export function createBoostGroupFormGroup(
  index: number,
  values: Partial<UrgencyBoostExtension.BoostGroup>,
  buildRulesFormArray: (
    rules: UrgencyBoostExtension.BoostGroup['productsInCart']['rules'],
  ) => FormArray<FormGroup<TypedRuleGroup>>,
): FormGroup<BoostGroupFormGroup> {
  const productsInCart = values.productsInCart ?? DEFAULT_BOOST_GROUP.productsInCart;
  return new FormGroup<BoostGroupFormGroup>({
    id: new FormControl(values.id ?? index, { nonNullable: true }),
    includeDigitalItems: new FormControl(
      values.includeDigitalItems ?? UrgencyBoostExtension.DEFAULT_BOOST_GROUP_INCLUDE_DIGITAL_ITEMS,
      { nonNullable: true },
    ),
    productsInCart: new FormGroup<ProductsInCartFormGroup>({
      mode: new FormControl(productsInCart.mode ?? 'smart', { nonNullable: true }),
      rules: buildRulesFormArray(productsInCart.rules ?? []),
      products: new FormControl(productsInCart.products ?? [], { nonNullable: true }),
      productsMatchMode: new FormControl(productsInCart.productsMatchMode ?? 'all', { nonNullable: true }),
    }),
    badges: new FormArray((values.badges ?? []).map((badge) => createBadgeFormGroup(badge))),
  });
}

/**
 * Caps the Recent Purchases window so the backend's paginated `orders` query
 * stays cheap. Maximum effective window is 7 days regardless of unit:
 *   - `days` → max 7
 *   - `hours` → max 168 (= 7 × 24)
 */
export const RECENT_PURCHASES_MAX_DAYS = 7;
export const RECENT_PURCHASES_MAX_HOURS = RECENT_PURCHASES_MAX_DAYS * 24;

// Group-level validator: re-runs whenever any control in the badge group
// emits, so the 7-day / 168-hour cap stays in sync with the windowUnit
// dropdown without a separate valueChanges subscription (which would leak
// every time the badge type toggles back into RECENT_PURCHASES).
export function recentPurchasesWindowMaxValidator(): ValidatorFn {
  return (control) => {
    const group = control as FormGroup;
    const valueControl = group.get('windowValue');
    if (!valueControl) return null;
    const unit = group.get('windowUnit')?.value as UrgencyBoostExtension.RecentPurchasesWindowUnit | undefined;
    const value = Number(valueControl.value);
    if (!Number.isFinite(value)) return null;
    const max = unit === 'days' ? RECENT_PURCHASES_MAX_DAYS : RECENT_PURCHASES_MAX_HOURS;
    return value > max ? { recentPurchasesMaxWindow: { max, actual: value } } : null;
  };
}

export function createBadgeFormGroup(badge: Partial<UrgencyBoostExtension.Badge>): FormGroup<BadgeFormGroup> {
  const type = (badge.type ?? UrgencyBoostExtension.BadgeType.CUSTOM_MESSAGE) as UrgencyBoostExtension.BadgeType;
  const group = new FormGroup<BadgeFormGroup>({
    id: new FormControl(badge.id ?? makeBadgeId(), { nonNullable: true }),
    type: new FormControl(type, { nonNullable: true }),
    richTextTemplate: new FormControl(badge.richTextTemplate ?? '', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(UrgencyBoostExtension.RICH_TEXT_TEMPLATE_MAX_LENGTH)],
    }),
    icon: new FormControl<string>(badge.icon ?? '', { nonNullable: true }),
    customIconUrl: new FormControl<string | File | null>(
      (badge as { customIconUrl?: string | File | null }).customIconUrl ?? null,
    ),
    backgroundColor: new FormControl(badge.backgroundColor ?? UrgencyBoostExtension.DEFAULT_BADGE_BACKGROUND_COLOR, {
      nonNullable: true,
    }),
    textColor: new FormControl(badge.textColor ?? UrgencyBoostExtension.DEFAULT_BADGE_TEXT_COLOR, {
      nonNullable: true,
    }),
    cornerRadius: new FormControl(badge.cornerRadius ?? UrgencyBoostExtension.DEFAULT_BADGE_CORNER_RADIUS, {
      nonNullable: true,
    }),
  });
  applyBadgeTypeFields(group, type, badge);
  return group;
}

export function applyBadgeTypeFields(
  group: FormGroup<BadgeFormGroup>,
  type: UrgencyBoostExtension.BadgeType,
  values: Partial<UrgencyBoostExtension.Badge> = {},
): void {
  const TYPE_FIELDS = ['name', 'minStock', 'maxStock', 'minPurchases', 'windowValue', 'windowUnit'] as const;
  for (const field of TYPE_FIELDS) {
    if (group.contains(field)) {
      group.removeControl(field);
    }
  }

  // No cross-field validators on the badge group — merchants are free to
  // save in any state. Per-field validators stay (required text, min/max
  // numeric bounds).
  group.setValidators([]);
  group.controls.richTextTemplate.updateValueAndValidity({ emitEvent: false });

  if (type === UrgencyBoostExtension.BadgeType.CUSTOM_MESSAGE) {
    const v = values as Partial<UrgencyBoostExtension.CustomMessageConfig>;
    group.addControl('name', new FormControl(v.name ?? '', { nonNullable: true }));
  }

  if (type === UrgencyBoostExtension.BadgeType.LOW_STOCK) {
    const v = values as Partial<UrgencyBoostExtension.LowStockConfig>;
    group.addControl(
      'minStock',
      new FormControl(v.minStock ?? 1, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0)],
      }),
    );
    group.addControl(
      'maxStock',
      new FormControl(v.maxStock ?? 10, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0)],
      }),
    );
  }

  if (type === UrgencyBoostExtension.BadgeType.RECENT_PURCHASES) {
    const v = values as Partial<UrgencyBoostExtension.RecentPurchasesConfig>;
    group.addControl(
      'minPurchases',
      new FormControl(v.minPurchases ?? 1, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0)],
      }),
    );
    group.addControl(
      'windowValue',
      new FormControl(v.windowValue ?? 24, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(1)],
      }),
    );
    group.addControl(
      'windowUnit',
      new FormControl<UrgencyBoostExtension.RecentPurchasesWindowUnit>(v.windowUnit ?? 'hours', {
        nonNullable: true,
      }),
    );
    // Group-level validator runs on every windowUnit/windowValue change,
    // replacing the per-control valueChanges subscription that previously
    // re-validated windowValue and leaked on every type toggle.
    group.setValidators([recentPurchasesWindowMaxValidator()]);
    group.updateValueAndValidity({ emitEvent: false });
  }
}
